// usePersistLocale — единая точка смены языка UI с персистом в users.language.
//
// Проблема, которую решает: setLocale (i18n/use-locale.ts) меняет только
// i18next + localStorage. Серверный контент (рекомендации, in-app уведомления,
// AI-генерации) строится по users.language в БД. Если переключать язык только
// через setLocale, UI и серверные тексты расходятся (UI на en, рекомендации на
// ru) — см. инцидент со смешением языков на дашборде.
//
// Этот хук синхронизирует ВСЕ три источника: i18next (мгновенный UI) +
// localStorage (кэш устройства) + users.language (PowerSync → БД, источник
// истины для серверного контента). Использовать его во всех переключателях
// языка вместо голого setLocale.

import { useCallback } from "react";

import { useSession } from "@/hooks/use-session";
import type { SupportedLocale } from "@/i18n/locales";
import { useLocale } from "@/i18n/use-locale";
import { getPowerSync } from "@/lib/powersync/client";

export function usePersistLocale(): (next: SupportedLocale) => Promise<void> {
	const { setLocale } = useLocale();
	const { user } = useSession();
	const userId = user?.id;

	return useCallback(
		async (next: SupportedLocale) => {
			// UI обновляется мгновенно даже без сессии/сети (гость на лендинге).
			await setLocale(next);
			if (!userId) return;
			try {
				await getPowerSync().execute(`UPDATE users SET language = ?, updated_at = ? WHERE id = ?`, [
					next,
					new Date().toISOString(),
					userId,
				]);
			} catch {
				// PowerSync ещё не инициализирован / офлайн — i18next+localStorage
				// уже применены; БД догонит при следующем переключении или через
				// LocaleSync на следующем cold start. Очередь записи не блокируем.
			}
		},
		[setLocale, userId],
	);
}
